import { useQuery } from '@tanstack/react-query';
import { CalendarRangeIcon, DownloadIcon, MailIcon, MonitorSmartphoneIcon, PencilLineIcon } from 'lucide-react';
import { useState } from 'react';
import { ErrorState } from '@/components/ErrorState';
import { LoadingBlock } from '@/components/LoadingBlock';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { downloadCsv } from '@/lib/csv';
import { fmtDateTime, fmtRelative } from '@/lib/format';
import { DELIVERY_STATUS_LABEL, payloadToCsv, RUN_STATUS_LABEL, RUN_TRIGGER_LABEL, type ReportDelivery, type ReportRun, type ReportsApi } from '@/lib/reports';
import { cn } from '@/lib/utils';
import { ReportPayloadView } from './ReportPayloadView';

interface Props {
  api: ReportsApi;
  runId: string | null;
  onClose: () => void;
  /** Super Users / job admins: see deliveries, edit the report, re-run for another period. */
  canManage: boolean;
  onEdit?: (subscriptionId: string) => void;
  onRunPeriod?: (subscriptionId: string) => void;
}

export function RunStatusBadge({ status }: { status: ReportRun['status'] | null }) {
  if (!status) return <span className="text-muted-foreground text-xs">never run</span>;
  const variant = status === 'sent' ? 'success' : status === 'failed' ? 'danger' : status === 'skipped' ? 'warning' : 'muted';
  return <Badge variant={variant}>{RUN_STATUS_LABEL[status]}</Badge>;
}

/** One run of a report: the rendered report, who received it, CSV download, and shortcuts back to the definition. */
export function RunDialog({ api, runId, onClose, canManage, onEdit, onRunPeriod }: Props) {
  const [tab, setTab] = useState<'report' | 'deliveries'>('report');
  const run = useQuery({ queryKey: ['reports', 'run', runId], queryFn: () => api.run_(runId as string), enabled: !!runId });
  const deliveries = useQuery({ queryKey: ['reports', 'run', runId, 'deliveries'], queryFn: () => api.deliveries(runId as string), enabled: !!runId && canManage && tab === 'deliveries' });
  const r = run.data?.run;

  return (
    <Dialog
      open={!!runId}
      onOpenChange={(o) => {
        if (!o) {
          onClose();
          setTab('report');
        }
      }}
    >
      <DialogContent className="flex max-h-[92vh] flex-col gap-0 overflow-hidden p-0 sm:max-w-3xl">
        <DialogHeader className="border-b px-6 pt-5 pb-4 pr-12">
          <DialogTitle className="text-lg">{r?.title ?? 'Report'}</DialogTitle>
          <DialogDescription className="flex flex-wrap items-center gap-x-2 gap-y-1">
            {r ? (
              <>
                <RunStatusBadge status={r.status} />
                <span>{RUN_TRIGGER_LABEL[r.triggeredBy]}</span>
                <span aria-hidden>·</span>
                <span title={fmtDateTime(r.startedAt)}>{fmtRelative(r.startedAt)}</span>
                {r.payload && (
                  <>
                    <span aria-hidden>·</span>
                    <span>
                      {r.payload.window.label} ({r.payload.window.timezone})
                    </span>
                  </>
                )}
                {r.status === 'sent' && (
                  <>
                    <span aria-hidden>·</span>
                    <span>
                      {r.recipientCount} {r.recipientCount === 1 ? 'recipient' : 'recipients'}
                      {r.emailCount ? `, ${r.emailCount} ${r.emailCount === 1 ? 'email' : 'emails'}` : ''}
                    </span>
                  </>
                )}
              </>
            ) : (
              'Loading…'
            )}
          </DialogDescription>
          {r?.error && (
            <p role="alert" className="text-destructive mt-1 text-xs">
              {r.error}
            </p>
          )}
          <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
            <div role="tablist" aria-label="Run detail" className="inline-flex border">
              {(['report', 'deliveries'] as const).map((t) => (
                <button key={t} role="tab" type="button" aria-selected={tab === t} disabled={t === 'deliveries' && !canManage} onClick={() => setTab(t)} className={cn('px-3 py-1.5 text-xs font-medium transition-colors disabled:opacity-40', tab === t ? 'bg-foreground text-background' : 'text-muted-foreground hover:bg-accent hover:text-foreground')}>
                  {t === 'report' ? 'Report' : 'Deliveries'}
                </button>
              ))}
            </div>
            <div className="flex flex-wrap items-center gap-1.5">
              {r?.payload && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const { filename, csv } = payloadToCsv(r.payload!, r.title);
                    downloadCsv(filename, csv);
                  }}
                >
                  <DownloadIcon aria-hidden /> CSV
                </Button>
              )}
              {canManage && r && onRunPeriod && (
                <Button type="button" variant="outline" size="sm" onClick={() => onRunPeriod(r.subscriptionId)}>
                  <CalendarRangeIcon aria-hidden /> Run for another period
                </Button>
              )}
              {canManage && r && onEdit && (
                <Button type="button" variant="outline" size="sm" onClick={() => onEdit(r.subscriptionId)}>
                  <PencilLineIcon aria-hidden /> Edit report
                </Button>
              )}
            </div>
          </div>
        </DialogHeader>
        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
          {run.isError ? <ErrorState error={run.error} onRetry={() => void run.refetch()} /> : !r ? <LoadingBlock rows={5} /> : tab === 'report' ? r.payload ? <ReportPayloadView payload={r.payload} compact /> : <p className="text-muted-foreground text-sm">This run has no stored report.</p> : <Deliveries items={deliveries.data?.items} loading={deliveries.isPending} error={deliveries.isError ? deliveries.error : null} />}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Deliveries({ items, loading, error }: { items: ReportDelivery[] | undefined; loading: boolean; error: unknown }) {
  if (error) return <ErrorState error={error} />;
  if (loading || !items) return <LoadingBlock rows={3} />;
  if (items.length === 0) return <p className="text-muted-foreground text-sm">Nothing was delivered for this run: it was quiet, a preview, or nobody matched the audience.</p>;
  const counts = items.reduce<Record<string, number>>((acc, d) => ({ ...acc, [d.status]: (acc[d.status] ?? 0) + 1 }), {});
  return (
    <div className="space-y-3">
      <p className="text-muted-foreground text-xs">
        {Object.entries(counts)
          .map(([k, n]) => `${n} ${DELIVERY_STATUS_LABEL[k as ReportDelivery['status']].toLowerCase()}`)
          .join(' · ')}
      </p>
      <div className="border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Recipient</TableHead>
              <TableHead>Channel</TableHead>
              <TableHead>Outcome</TableHead>
              <TableHead>Opened</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((d) => (
              <TableRow key={d.id}>
                <TableCell>
                  <div className="font-medium">{d.name}</div>
                  {d.email && d.email !== d.name && <div className="text-muted-foreground text-xs">{d.email}</div>}
                </TableCell>
                <TableCell className="text-sm">
                  <span className="flex items-center gap-1.5">
                    {d.channel === 'email' ? <MailIcon className="text-muted-foreground size-3.5" aria-hidden /> : <MonitorSmartphoneIcon className="text-muted-foreground size-3.5" aria-hidden />}
                    {d.channel === 'email' ? 'Email' : 'In-app'}
                  </span>
                </TableCell>
                <TableCell>
                  <Badge variant={d.status === 'sent' ? 'success' : d.status === 'failed' ? 'danger' : 'muted'}>{DELIVERY_STATUS_LABEL[d.status]}</Badge>
                  {d.error && <div className="text-destructive max-w-64 truncate text-xs" title={d.error}>{d.error}</div>}
                </TableCell>
                <TableCell className="text-muted-foreground text-xs">{d.channel === 'in_app' ? (d.readAt ? fmtRelative(d.readAt) : 'not yet') : '—'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
